import { useState, type FormEvent } from 'react';
import { Fingerprint, KeyRound } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import {
  beginPasskeyLogin,
  finishPasskeyLogin,
  loginWithRecoveryCode,
} from '../api/auth';
import type { AuthSession } from '../store/auth';
import { getApiClient } from '../api/factory';
import { ApiError } from '../api/client';
import { useT, type TFn } from '../i18n';

interface Props {
  mfaSession: string;
  onSuccess: (session: AuthSession) => Promise<void> | void;
}

type Mode = 'choose' | 'passkey' | 'recovery';

// Второй шаг login: passkey (через WebAuthn) или recovery-код.
// Mode 'choose' = две большие кнопки; 'recovery' = форма ввода.
// 'passkey' — промежуточное состояние пока authenticator подтверждает.
export function MfaStep({ mfaSession, onSuccess }: Props) {
  const t = useT();
  const [mode, setMode] = useState<Mode>('choose');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function handlePasskey() {
    setError(null);
    setPending(true);
    setMode('passkey');
    try {
      const begin = await beginPasskeyLogin(getApiClient(), mfaSession);
      const opts = begin.options as { publicKey: PublicKeyCredentialRequestOptionsJSON };
      const assertion = await startAuthentication({ optionsJSON: opts.publicKey });
      const session = await finishPasskeyLogin(getApiClient(), {
        sessionToken: begin.sessionToken,
        assertion,
      });
      await onSuccess(session);
    } catch (e) {
      setMode('choose');
      setError(passkeyError(e, t));
    } finally {
      setPending(false);
    }
  }

  async function handleRecovery(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!recoveryCode.trim()) return;
    setError(null);
    setPending(true);
    try {
      const session = await loginWithRecoveryCode(getApiClient(), {
        mfaSession,
        code: recoveryCode.trim(),
      });
      await onSuccess(session);
    } catch (err) {
      setError(recoveryError(err, t));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-900">{t('auth.mfa.title')}</h2>
        <p className="text-sm text-slate-500">{t('auth.mfa.subtitle')}</p>
      </div>

      {mode === 'passkey' && (
        <p className="text-sm text-slate-600">{t('auth.mfa.starting')}</p>
      )}

      {mode === 'choose' && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={pending}
            onClick={handlePasskey}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-accent-600 px-3 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            <Fingerprint className="h-4 w-4" />
            {t('auth.mfa.usePasskey')}
          </button>
          <button
            type="button"
            onClick={() => setMode('recovery')}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <KeyRound className="h-4 w-4" />
            {t('auth.mfa.useRecovery')}
          </button>
        </div>
      )}

      {mode === 'recovery' && (
        <form onSubmit={handleRecovery} className="space-y-2">
          <label className="block text-sm text-slate-700">
            {t('auth.mfa.recoveryPrompt')}
            <input
              type="text"
              autoFocus
              autoComplete="one-time-code"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
            />
          </label>
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={pending || !recoveryCode.trim()}
              className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
            >
              {t('auth.mfa.recoverySubmit')}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('choose');
                setError(null);
              }}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function passkeyError(e: unknown, t: TFn): string {
  if (e instanceof ApiError) return t('auth.mfa.error');
  // WebAuthn DOMException — пользователь отменил тач, тайм-аут и т.п.
  if (e instanceof Error) {
    if (e.name === 'NotAllowedError' || e.name === 'AbortError') return t('common.cancel');
    return t('auth.mfa.error');
  }
  return t('common.unknownError');
}

function recoveryError(e: unknown, t: TFn): string {
  if (e instanceof ApiError) {
    if (e.code === 'auth_failed') return t('auth.mfa.invalidCode');
    return t('auth.mfa.error');
  }
  return t('common.unknownError');
}
